import React from 'react'

import clsx from 'clsx'

import CollapsibleText from 'components/CollapsibleText'
import CtaButton from 'components/CtaButton'
import Paper from 'components/Paper'
import { useEventContext } from 'context/EventContext'
import { getListingFacetNames } from 'helpers/listingHelpers'

import { CATEGORIES_FACETS, LIMIT_OVERVIEW_LENGTH } from './constants'
import AttributeList, { ATTRIBUTE_VARIANTS } from '../AttributeList'
import GridContainer from '../GridContainer'
import GridItem from '../GridItem'

import classes from './Overview.module.scss'

const titleAttribute = title => [
  {
    label: title,
    variant: ATTRIBUTE_VARIANTS.none,
    isBold: true,
  },
]

const attributes = (categoryFacets, listingCategoryFacetsNames) =>
  categoryFacets.map(facet => ({
    label: facet.text,
    variant: listingCategoryFacetsNames.includes(facet.name) ? ATTRIBUTE_VARIANTS.check : ATTRIBUTE_VARIANTS.uncheck,
  }))

const attributesWithVideoTurorials = (categoryFacets, listingCategoryFacetsNames, hasTrainingVideos) =>
  categoryFacets.map(facet => ({
    label: facet.text,
    variant:
      listingCategoryFacetsNames.includes(facet.name) || (facet.text === 'Video tutorials' && hasTrainingVideos)
        ? ATTRIBUTE_VARIANTS.check
        : ATTRIBUTE_VARIANTS.uncheck,
  }))

/** @type {import("./types").default} */
const Overview = ({ hideDescription, isCtaFullWidth = false, isCtaTextButton = false, listings }) => {
  const gridSize = listings.length
  const { addEvent, sendEvent } = useEventContext({
    engagementComponent: 'product_overview',
    name: 'engagement_product_overview',
  })

  return (
    <>
      <GridContainer>
        {listings.map(listing => (
          <GridItem key={`overview-content-${listing.id}`}>
            <Paper elevation={0} square className={clsx(classes.root, classes.firstPaper)}>
              <div className={clsx(classes.header, { [classes.hideDescription]: hideDescription })}>
                {hideDescription ? null : (
                  <CollapsibleText
                    text={listing.description}
                    maxNumberChars={LIMIT_OVERVIEW_LENGTH}
                    onButtonClick={(_, { isExpanded }) => {
                      sendEvent({
                        engagementDestination: isExpanded ? 'expand-text' : 'collapse-text',
                        engagementDetails: isExpanded ? 'text-link_read-more' : 'text-link_read-less',
                        name: 'engagement_product_click',
                      })
                    }}
                  />
                )}
              </div>
            </Paper>
          </GridItem>
        ))}
      </GridContainer>

      <GridContainer>
        {listings.map((listing, index) => (
          <GridItem key={`overview-content-${listing.id}`}>
            <Paper elevation={0} square className={clsx(classes.root, classes.lastPaper)}>
              <div className={classes.attributeListRelative}>
                <AttributeList
                  attributes={titleAttribute('Platforms supported')}
                  gridSize={gridSize}
                  gridIndex={index}
                />
              </div>
              <div className={clsx(classes.attributesList, classes.attributeListRelative)}>
                <AttributeList
                  attributes={attributes(
                    CATEGORIES_FACETS.platformsSupported,
                    getListingFacetNames(listing, 'platforms')
                  )}
                  gridSize={gridSize}
                  gridIndex={index}
                />
              </div>
              <div className={classes.attributeListRelative}>
                <AttributeList attributes={titleAttribute('Typical customers')} gridSize={gridSize} gridIndex={index} />
              </div>
              <div className={clsx(classes.attributesList, classes.attributeListRelative)}>
                <AttributeList
                  attributes={attributes(
                    CATEGORIES_FACETS.typicalCustomers,
                    getListingFacetNames(listing, 'typical_customers')
                  )}
                  gridSize={gridSize}
                  gridIndex={index}
                />
              </div>
              <div className={classes.attributeListRelative}>
                <AttributeList attributes={titleAttribute('Customer support')} gridSize={gridSize} gridIndex={index} />
              </div>
              <div className={clsx(classes.lastAttributeList, classes.attributeListRelative)}>
                <AttributeList
                  attributes={attributesWithVideoTurorials(
                    CATEGORIES_FACETS.supports,
                    listing.supports,
                    listing.training_options?.includes('Videos')
                  )}
                  gridSize={gridSize}
                  gridIndex={index}
                />
              </div>
              <div className={classes.button}>
                <CtaButton
                  fullWidth={isCtaFullWidth}
                  label="view more details"
                  shortLabel="view details"
                  secondary
                  href={listing.cta_urls.detail}
                  variant={isCtaTextButton ? 'text' : 'contained'}
                  data-testid="overview-cta"
                  {...addEvent({
                    engagementDestination: 'go-to_product_details',
                    engagementDetails: 'button_view-more-details',
                    name: 'engagement_product_click',
                  })}
                />
              </div>
            </Paper>
          </GridItem>
        ))}
      </GridContainer>
    </>
  )
}

export default Overview
